import { inject, NgZone } from '@angular/core';
import { filter, take } from 'rxjs';
import {
    EventBusBridgeService,
    EVENT_TYPES,
    ShellAuthLoginPayload,
    ShellAuthLogoutPayload,
    ShellAuthTokenRefreshedPayload,
    ShellOrganizationChangedPayload,
    ShellConfigUpdatedPayload,
    WebgovNavigationRequestPayload,
    WebgovNavigationChangedPayload,
} from '@client/app/services/event-bus-bridge.service';
import { MfeDataService } from '@monsido/core/mfe/mfe-data.service';
import { MonEventService } from '@monsido/ng2/services/mon-event/mon-event.service';
import { SessionService } from '@monsido/core/session/session.service';
import { StateService, TransitionService, UrlService, StateDeclaration, RawParams } from '@uirouter/core';
import { IS_MICROFRONTEND, SHADOW_ROOT_TOKEN } from '@client/mfe/tokens/mfe-tokens';
import { ApiService } from '@client/app/services/api/api-service/api.service';
import { CoreAgreementService } from '@monsido/core/session/services/agreement/agreement.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { DialogService } from '@monsido/angular-shared-components';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
    buildShellPathIndex,
    matchShellPath,
    buildOutgoingInternalRoute,
    extractCustomerId,
    ShellRouteIndex,
    ShellRouteMatch,
} from '@client/app/services/shell-route-registry';
import { normalizeShellPathForUrl } from '@client/mfe/bootstrap/mfe-navigation';
import {
    resetNavigationCoordinator,
    markEventNavigationReceived,
    setPendingShellNavigation,
    getPendingShellNavigation,
    consumePendingShellNavigation,
    clearPendingShellNavigationBySeq,
    shouldSuppressDashboardPublish,
} from '@client/app/services/navigation-coordinator';

/**
 * Build a fresh shell-route index per navigation request.
 *
 * The previous memoised index (rebuilt only when `stateService.get().length`
 * changed) opened a stale-index window whenever a lazy feature module
 * registered its concrete states after a null-match had already been served.
 * Rebuilding on every request is O(n) over ~100 registered states and trivial
 * compared to the network-bound work a navigation triggers — correctness over
 * micro-optimisation.
 */
function getShellRouteIndex (stateService: StateService): ShellRouteIndex {
    return buildShellPathIndex(stateService.get());
}

const MENU_ITEMS = [
    { label: 'Domains', route: '/domains', icon: 'pi pi-globe', feature: null as string | null },
    { label: 'Heatmaps', route: '/heatmaps', icon: 'pi pi-bolt', feature: 'admin_heatmaps' },
    { label: 'Global Policies', route: '/global-policies', icon: 'pi pi-shield', feature: 'policies_all' },
    { label: 'Reports', route: '/reports', icon: 'pi pi-chart-bar', feature: 'report_center_all' },
    { label: 'Admin', route: '/admin', icon: 'pi pi-cog', feature: 'customer_admin' },
];

function filterMenuItems (activeFeatureService: ActiveFeatureService): Array<{ label: string; route: string; icon: string }> {
    return MENU_ITEMS.filter((item) => {
        if (item.feature === null) {
            return true;
        }
        return activeFeatureService.isFeatureActivePure(item.feature);
    });
}


/**
 * Initialises the event bus bridge. Called from provideAppInitializer in AppModule.
 * No-op in standalone mode.
 */
export function initEventBusBridge (): void {
    const isMfe = inject(IS_MICROFRONTEND, { optional: true });
    const eventBus = inject(EventBusBridgeService);

    if (!isMfe || !eventBus.isConnected) {
        return;
    }

    const mfeData = inject(MfeDataService);
    const sessionService = inject(SessionService);
    const stateService = inject(StateService);
    const transitionService = inject(TransitionService);
    const urlService = inject(UrlService);
    const ngZone = inject(NgZone);
    const monEventService = inject(MonEventService);
    const apiService = inject(ApiService);
    const coreAgreementService = inject(CoreAgreementService);
    const activeFeatureService = inject(ActiveFeatureService);
    const monDialog = inject(MonDialog);
    const sharedDialogService = inject(DialogService);
    const ngbModal = inject(NgbModal);
    const shadowRootToken = inject(SHADOW_ROOT_TOKEN, { optional: true });

    // Reset coordinator on each mount so no stale state leaks across remounts.
    resetNavigationCoordinator();

    /**
     * Latest-request-wins bookkeeping for queued navigation paths when no
     * customer is loaded yet (sessionService.customer === null). The most
     * recent WEBGOV_NAVIGATION_REQUEST replaces any earlier queued path so a
     * burst of CTAs never replays stale URLs.
     */
    let queuedNavigationPath: string | null = null;
    let customerQueueSubscriptionActive = false;

    const queueNavigationForCustomer = (path: string): void => {
        queuedNavigationPath = path;
        if (customerQueueSubscriptionActive) {
            return;
        }
        customerQueueSubscriptionActive = true;
        sessionService.customer$.pipe(
            filter((c) => c?.id != null),
            take(1),
        ).subscribe(() => {
            customerQueueSubscriptionActive = false;
            const queued = queuedNavigationPath;
            queuedNavigationPath = null;
            if (queued === null) {
                return;
            }
            // Re-dispatch the queued navigation now that a customer is loaded.
            // We synthesise the same handling the live request would have
            // taken by re-running the handler body with the latest payload.
            handleNavigationRequest({ path: queued }, { skipSync: true });

            // Guard: the queue flush fires inside the customer-picker's
            // setAgreement() flow (synchronous customer$ emission). After
            // setAgreement returns, the customer-picker navigates to
            // base.customer.dashboard, superseding the CTA's transition
            // (whose validateCustomer resolve is still in-flight).
            //
            // EAGER: intercept the dashboard transition with onBefore and
            //        redirect to the CTA target (dashboard never renders).
            // LAZY:  concrete state unknown, can't redirect. Retry the URL
            //        via onSuccess after the dashboard settles.
            const ctaPending = getPendingShellNavigation();
            if (ctaPending && ctaPending.stateName && ctaPending.stateName !== 'base.customer.dashboard') {
                // EAGER guard — redirect dashboard → CTA target
                const ctaTarget = ctaPending.stateName;
                const ctaParams = { ...ctaPending.params } as RawParams;
                const ctaReload = ctaTarget;

                const deregGuard = transitionService.onBefore(
                    { to: 'base.customer.dashboard' },
                    () => {
                        return stateService.target(ctaTarget, ctaParams as RawParams, {
                            location: false as const,
                            reload: ctaReload,
                            inherit: false,
                        });
                    },
                );

                /* eslint-disable @typescript-eslint/no-use-before-define -- circular deregistration pair */
                const cleanup = (): void => {
                    deregGuard();
                    deregLanding();
                };
                /* eslint-enable @typescript-eslint/no-use-before-define */

                const deregLanding = transitionService.onSuccess({}, (t) => {
                    if (t.to().name === ctaTarget) {
                        cleanup();
                    }
                });
                setTimeout(cleanup, 10000);
            } else if (!ctaPending) {
                // LAZY: the queue flush above fired urlService.sync() which
                // starts a transition to the accessibility.** future state.
                // The customer-picker fires go('base.customer.dashboard')
                // shortly after, SUPERSEDING the accessibility transition.
                // Even though our onBefore blocks the dashboard (type 3 abort),
                // the accessibility transition stays superseded — its lazyLoad
                // retry redirect is silently ignored by UI-Router.
                //
                // Fix: after blocking the dashboard, re-fire urlService.sync()
                // so a fresh transition picks up the concrete states that the
                // (completed or still-in-flight) lazyLoad registered.
                const shellSegment = normalizeShellPathForUrl(queued);
                const { path: pathWithoutCustomer, customerId: extractedCid } = extractCustomerId(
                    shellSegment.startsWith('/') ? shellSegment : `/${shellSegment}`,
                );
                const effectiveCid = extractedCid ??
                    (sessionService.customer?.id != null ? String(sessionService.customer.id) : undefined);
                const lazyInternalUrl = effectiveCid
                    ? `/${effectiveCid}/${pathWithoutCustomer.replace(/^\//, '')}`
                    : null;

                /* eslint-disable @typescript-eslint/no-use-before-define -- circular deregistration pair */
                const cleanupLazy = (): void => {
                    deregBlock();
                    deregLanding();
                };
                /* eslint-enable @typescript-eslint/no-use-before-define */

                const deregBlock = transitionService.onBefore(
                    { to: 'base.customer.dashboard' },
                    () => {
                        cleanupLazy();
                        if (lazyInternalUrl) {
                            setTimeout(() => {
                                urlService.url(lazyInternalUrl);
                                urlService.sync();
                            }, 0);
                        }
                        return false;
                    },
                );

                const deregLanding = transitionService.onSuccess({}, (t) => {
                    const name = t.to().name;
                    if (name !== 'base.customer.dashboard' && name !== 'base.dashboard') {
                        cleanupLazy();
                    }
                });

                setTimeout(cleanupLazy, 10000);
            }
        });
    };

    // Auth: login
    eventBus.subscribe<ShellAuthLoginPayload>(
        EVENT_TYPES.SHELL_AUTH_LOGIN,
        (event) => {
            const p = event.payload;
            if (!p) {
                return;
            }
            // eslint-disable-next-line no-console
            console.log('[WebGov MFE] Auth login received from shell', p);
            mfeData.setProperty('user', p.user);
            // MERGE into the existing config — do NOT replace. The element already
            // received config = { locale, apiBaseUrl, accessToken } from the CC
            // property; replacing it with { accessToken } would wipe apiBaseUrl and
            // locale. setProperty('config', …) normalizes apiBaseUrl → apiUrl.
            const currentConfig = mfeData.config$.getValue();
            mfeData.setProperty('config', {
                ...currentConfig,
                accessToken: p.token,
                expiresAt: p.expiresAt,
            });
        },
    );

    // Auth: token refreshed
    eventBus.subscribe<ShellAuthTokenRefreshedPayload>(
        EVENT_TYPES.SHELL_AUTH_TOKEN_REFRESHED,
        (event) => {
            const p = event.payload;
            if (!p) {
                return;
            }
            const currentConfig = mfeData.config$.getValue();
            mfeData.setProperty('config', {
                ...currentConfig,
                accessToken: p.token,
            });
            monEventService.run('newAccessToken');
        },
    );

    // Auth: logout
    eventBus.subscribe<ShellAuthLogoutPayload>(
        EVENT_TYPES.SHELL_AUTH_LOGOUT,
        (event) => {
            // TODO: Remove console log and handle the event when needed
            // eslint-disable-next-line no-console
            console.log('[WebGov MFE] Logout received, reason:', event.payload?.reason);
            mfeData.setProperty('user', null);
            mfeData.setProperty('config', null);
        },
    );

    // Organization changed — destroy and re-bootstrap the Angular platform so that
    // all open dialogs (MonDialog + PrimeNG), overlays, service caches, subscriptions,
    // and component state are fully reset for the incoming organisation.
    //
    // Why remount instead of surgical cleanup:
    //   MonDialog.closeAll() schedules actual DOM removal in a 500ms setTimeout, so
    //   dialogs are still present when navigation fires.  PrimeNG dialogs opened with
    //   appendTo="body" (redirected to mfe-root) survive Angular component destruction
    //   because their DOM was moved outside the component tree.  A platform destroy +
    //   re-bootstrap wipes ALL of this in one shot with no edge cases.
    //
    // The new org properties are forwarded to the element BEFORE remount so they are
    // replayed as pending properties during the fresh bootstrap (see _pendingProperties
    // in webgov-element.ts).
    eventBus.subscribe<ShellOrganizationChangedPayload>(
        EVENT_TYPES.SHELL_ORGANIZATION_CHANGED,
        (event) => {
            const p = event.payload;
            if (!p) {
                return;
            }
            // eslint-disable-next-line no-console
            console.log('[WebGov MFE] Organization changed — remounting for org:', p.organizationId);

            // Locate the <mfe-webgov> host element via the injected ShadowRoot.
            // (shadowRootToken was captured at injection-context time, above.)
            const hostElement = shadowRootToken?.host as (HTMLElement & {
                remount?: () => void;
                organizationId?: string;
                organizationName?: string;
            }) | null;

            if (hostElement && typeof hostElement.remount === 'function') {
                // 1. Update the API header immediately so any in-flight re-bootstrap
                //    requests carry the correct org UUID from the start.
                apiService.setHeader('Monsido-Account-Id', p.organizationId);

                // 2. Write the new org data to the element's OWN property bag
                //    (hostElement.organizationId / .organizationName setters update
                //    this._properties, which remount() snapshots BEFORE destroying
                //    the old platform and queues as _pendingProperties for the fresh
                //    bootstrap).  Do NOT call mfeData.setProperty() here — that would
                //    write into the about-to-be-destroyed MfeDataService instance and
                //    the new bootstrap would never see the values.
                hostElement.organizationId = p.organizationId;
                hostElement.organizationName = p.organizationName;

                // 3. Destroy the Angular platform and re-bootstrap a clean app.
                //    remount() snapshots all this._properties (user, config/token,
                //    orgId, etc.) before destroying, then replays them into the new
                //    MfeDataService via _pendingProperties.  The new initEventBusBridge
                //    calls requestAuthState() so the shell re-sends the current token,
                //    resolving waitForInitialRootContext and unblocking navigation.
                hostElement.remount();
            } else {
                // Fallback for non-MFE / standalone mode.
                // eslint-disable-next-line no-console
                console.warn('[WebGov MFE] remount() not available — falling back to clearAgreement + navigate');
                apiService.setHeader('Monsido-Account-Id', p.organizationId);
                mfeData.setProperty('organizationId', p.organizationId);
                mfeData.setProperty('organizationName', p.organizationName);
                monDialog.closeAll(undefined, true);
                ngZone.run(() => {
                    coreAgreementService.clearAgreement().then(() => {
                        stateService.go('base.dashboard');
                    });
                });
            }
        },
    );

    // Config updated
    eventBus.subscribe<ShellConfigUpdatedPayload>(
        EVENT_TYPES.SHELL_CONFIG_UPDATED,
        (event) => {
            const p = event.payload;
            if (!p) {
                return;
            }
            // TODO: Remove console log and handle the event when needed
            // eslint-disable-next-line no-console
            console.log('[WebGov MFE] Config updated from shell');
            const currentConfig = mfeData.config$.getValue();
            mfeData.setProperty('config', { ...currentConfig, ...p });
        },
    );

    // Theme changed
    eventBus.subscribe(
        EVENT_TYPES.SHELL_THEME_CHANGED,
        (event) => {
            const theme = (event.payload as { theme?: string })?.theme;
            if (theme) {
                mfeData.setProperty('theme', theme);
            }
        },
    );

    // Shell → MFE navigation requests.
    //
    // Extracted into a named function so the customer-null queue (Fix C) can
    // re-dispatch the most recent request once the session customer loads.
    // Declared as a hoisted function expression so the queue callback above
    // can invoke it without a forward-reference error.
    function handleNavigationRequest (p: WebgovNavigationRequestPayload, opts?: { skipSync?: boolean }): void {
        if (!p) {
            return;
        }

        // Null match usually means a lazy-loaded feature module whose
        // concrete state isn't registered yet — handled via internal-URL fallback.
        const index = getShellRouteIndex(stateService);
        const match: ShellRouteMatch | null = matchShellPath(index, p.path);

        // CC-provided URL customerId (when present) takes precedence over the
        // session value: CC is authoritative for the navigation target. Only
        // fall back to the session customer when the path carries no prefix.
        const { customerId: urlCustomerId } = extractCustomerId(
            // Strip query string + /webgov prefix the same way matchShellPath does.
            (() => {
                let path = p.path.split('?')[0];
                if (path.startsWith('/webgov')) {
                    path = path.slice('/webgov'.length) || '/';
                }
                if (!path.startsWith('/')) {
                    path = '/' + path;
                }
                return path;
            })(),
        );

        const sessionCustomerId = sessionService.customer?.id;
        const customerId = urlCustomerId ?? (sessionCustomerId != null ? String(sessionCustomerId) : undefined);

        // Signal that an event-based navigation has arrived this mount.
        // The bootstrap deep-link driver checks this and defers.
        markEventNavigationReceived();

        if (customerId == null) {
            // No customer anywhere — queue the latest path and flush once the
            // session customer becomes available. Prevents URL/app desync where
            // CC already updated the browser URL but the MFE silently dropped
            // the navigation.
            queueNavigationForCustomer(p.path);
            return;
        }

        // Close all open dialogs before navigating.
        // In standalone, modals block navigation (the user must close them manually).
        // In MFE, the CC shell menu bypasses that and would leave dialogs orphaned.
        //
        // Dialog systems used in this app:
        //   1. MonDialog (legacy custom dialog wrapper)
        //   2. @monsido/angular-shared-components DialogService (legacy ng-bootstrap wrapper)
        //   3. NgbModal (@ng-bootstrap/ng-bootstrap)
        //   4. PrimeNG DialogService (primeng/dynamicdialog)
        //
        // NOTE: PrimeNG DialogService is provided at component-level in several components
        // (e.g. top-navigation.component.ts providers: [DialogService]).
        // This means each component gets its own instance — the root DI token has no refs.
        // We close PrimeNG dialogs via DOM: find all <p-dynamicdialog> in the shadow root
        // and click their close buttons, which triggers the normal PrimeNG close lifecycle.
        monDialog.closeAll(undefined, true);
        sharedDialogService.closeAll();
        ngbModal.dismissAll('mfe-navigation');

        // PrimeNG: close all open PrimeNG overlays via DOM.
        // Three PrimeNG dialog types are used in the app:
        //
        //   a) <p-dynamicdialog> — programmatically opened via DialogService.open().
        //      Close by clicking the header close button.
        //
        //   b) <p-confirmdialog> — opened via ConfirmationService.confirm().
        //      ConfirmationService is also component-scoped so we cannot use DI.
        //      Treat navigation as a rejection: click the reject/cancel button,
        //      falling back to the header close button if there is none.
        //
        //   c) <p-dialog visible=true> — inline template dialogs bound to a
        //      component signal/property. Angular destroys these when the component
        //      is destroyed on navigation, so no explicit action is needed here.
        //      We still close them defensively via the header close button.
        //
        // All three share the same close-button selector pattern in PrimeNG 19.
        if (shadowRootToken) {
            const dialogSelectors = 'p-dynamicdialog, p-confirmdialog, p-confirm-dialog';
            shadowRootToken.querySelectorAll<HTMLElement>(dialogSelectors).forEach((dialog) => {
                // For p-confirmdialog: prefer reject/cancel (treat navigation as cancel)
                // For p-dynamicdialog / p-dialog: use the header close button
                const rejectBtn = dialog.querySelector<HTMLElement>(
                    '[data-pc-name="rejectbutton"], .p-confirmdialog-reject-button',
                );
                const closeBtn = dialog.querySelector<HTMLElement>(
                    '.p-dialog-close-button, button[aria-label="Close"]',
                );
                (rejectBtn ?? closeBtn)?.click();
            });
        }

        // EAGER branch: register pending BEFORE ngZone.run() so the onSuccess
        // hook sees it even if a transition completes via the microtask queue
        // before the zone callback fires.
        if (match) {
            const goParams: RawParams = { ...match.params, customerId };
            const eagerSeq = setPendingShellNavigation(match.stateName, { ...goParams });

            const goOptions = { location: false as const, reload: match.stateName, inherit: false };

            ngZone.run(() => {
                stateService.go(match.stateName, goParams, goOptions)
                    ?.catch(() => {
                        clearPendingShellNavigationBySeq(eagerSeq);
                    });
            });
            return;
        }

        // LAZY: concrete state not registered. Drive the MFE internal URL
        // so UI-Router matches the '**' future state and lazyLoads it.
        // No pending suppression — let onSuccess publish navigation.changed
        // so CC learns the final resolved destination after lazy loading.
        ngZone.run(() => {
            const shellSegment = normalizeShellPathForUrl(p.path);
            const { path: pathWithoutCustomer, customerId: extractedId } = extractCustomerId(
                shellSegment.startsWith('/') ? shellSegment : `/${shellSegment}`,
            );
            const effectiveCustomerId = extractedId ?? customerId;
            const cleanedSegment = pathWithoutCustomer.replace(/^\//, '');

            const internalUrl = `/${effectiveCustomerId}/${cleanedSegment}`;
            urlService.url(internalUrl);
            if (!opts?.skipSync) {
                urlService.sync();
            }
        });
    }

    eventBus.subscribe<WebgovNavigationRequestPayload>(
        EVENT_TYPES.WEBGOV_NAVIGATION_REQUEST,
        (event) => {
            handleNavigationRequest(event.payload as WebgovNavigationRequestPayload);
        },
    );

    // Request auth state & announce ready
    eventBus.requestAuthState();
    eventBus.announceReady();

    // Reactively register navigation items in the command-center shell.
    // No customer ID in the payload — the shell just needs labels, routes, and icons.
    // Menu items are filtered based on the customer's active features, matching
    // the same logic used in TopMenuComponent.setuptemplateConditions.
    // Republishes on every customer change (including organization switches).
    sessionService.customer$.pipe(
        filter((c) => c?.id != null),
    ).subscribe(() => {
        // eslint-disable-next-line no-console
        console.log('[WebGov MFE] Customer set — publishing menu items');
        eventBus.publishMenuReady(filterMenuItems(activeFeatureService));
    });

    // MFE → Shell: publish navigation changed on every successful UI-Router transition.
    // The hook fires after the transition completes so the URL and state are settled.
    // Note: the deregistration function returned by onSuccess is intentionally not stored —
    // on MFE remount the entire Angular platform (and its TransitionService) is destroyed,
    // so hooks cannot leak across mounts.
    transitionService.onSuccess({}, (transition) => {
        const toState: StateDeclaration = transition.to();
        const toParams: RawParams = transition.params('to');
        const stateName = toState.name ?? '';

        // 1. Shell-driven navigation completing — suppress echo to CC.
        if (consumePendingShellNavigation(stateName, toParams as Record<string, unknown>)) {
            return;
        }

        // 2. Intermediate dashboard during a pending non-dashboard navigation.
        const pending = getPendingShellNavigation();
        if (
            pending !== null &&
            pending.stateName !== 'base.customer.dashboard' &&
            stateName === 'base.customer.dashboard'
        ) {
            return;
        }

        // 3. Deep-link redirect dashboard suppression.
        if (shouldSuppressDashboardPublish(stateName)) {
            return;
        }

        const internalRoute = buildOutgoingInternalRoute(toState, toParams as Record<string, unknown>);
        if (internalRoute === null) {
            return;
        }

        // Strip empty query params (e.g. '?search=' → '').
        const cleanedRoute = internalRoute
            .replace(/[?&][^=]*=(?=&|$)/g, '')
            .replace(/\?$/, '');

        const fullPath = '/webgov' + cleanedRoute;
        const isCustomerScoped = stateName.startsWith('base.customer.');
        const domainTitle = isCustomerScoped ? (sessionService.domain?.title ?? undefined) : undefined;

        const payload: WebgovNavigationChangedPayload = domainTitle !== undefined
            ? { internalRoute: cleanedRoute, fullPath, breadcrumbLabel: domainTitle }
            : { internalRoute: cleanedRoute, fullPath };

        eventBus.publishNavigationChanged(payload);
    });

    // Emit once immediately for the current state so that the CC shell breadcrumbs
    // and menu highlighting are correct on page reload / initial mount.
    {
        const currentState: StateDeclaration = stateService.current;
        const currentParams: RawParams = stateService.params as RawParams;
        const stateName = currentState.name ?? '';
        if (stateName) {
            const internalRoute = buildOutgoingInternalRoute(currentState, currentParams as Record<string, unknown>);
            if (internalRoute !== null) {
                const cleanedRoute = internalRoute
                    .replace(/[?&][^=]*=(?=&|$)/g, '')
                    .replace(/\?$/, '');
                const fullPath = '/webgov' + cleanedRoute;
                const isCustomerScoped = stateName.startsWith('base.customer.');
                const domainTitle = isCustomerScoped ? (sessionService.domain?.title ?? undefined) : undefined;

                const initialPayload: WebgovNavigationChangedPayload = domainTitle !== undefined
                    ? { internalRoute: cleanedRoute, fullPath, breadcrumbLabel: domainTitle }
                    : { internalRoute: cleanedRoute, fullPath };

                eventBus.publishNavigationChanged(initialPayload);
            }
        }
    }

    // TODO: Remove console log when implementation is complete
    // eslint-disable-next-line no-console
    console.log('[WebGov MFE] Event bus bridge initialized — listening for shell events');
}
